import { BookmarkIcon, BookmarkSlashIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { format, formatDistanceToNow } from 'date-fns'
import { Tooltip } from 'flowbite-react'
import { Fragment, memo, useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import { getInputStyles } from '../../../components/Form/styles'
import { Note } from '../../../apis/types'
import { Link } from 'react-router-dom'
import { splitNoteBody } from '../noteBody'
import TagChooser from './TagChooser'

function highlightSearch(text: string, searchTerm?: string) {
  if (!searchTerm) {
    return text
  }
  return text.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, i) =>
    part.toLowerCase() === searchTerm.toLowerCase() ? (
      <mark key={i} className="text-yellow-800 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-500/30">
        {part}
      </mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  )
}

function NoteBodyText({ body, searchTerm }: { body: string; searchTerm?: string }) {
  return (
    <>
      {splitNoteBody(body).map((segment, i) =>
        segment.type === 'url' ? (
          <a
            key={i}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-indigo-500 hover:underline dark:text-indigo-300"
          >
            {highlightSearch(segment.href, searchTerm)}
          </a>
        ) : (
          <Fragment key={i}>{highlightSearch(segment.value, searchTerm)}</Fragment>
        )
      )}
    </>
  )
}

const NoteItem = memo(
  ({
    note,
    canModify,
    searchTerm,
    onUpdate,
    onDelete,
    onTogglePin,
  }: {
    note: Note
    isLast: boolean
    canModify: boolean
    searchTerm?: string
    onUpdate: (noteCuid: string, body: string, tags: string[]) => Promise<void>
    onDelete: (note: Note) => Promise<void>
    onTogglePin: (note: Note) => Promise<void>
  }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editedBody, setEditedBody] = useState(note.body)
    const editInputRef = useRef<HTMLTextAreaElement>(null)
    const [sourceEntityType, sourceEntityCuid] = note.sourceEntity.split('|')
    const isExternalAuthor = note.createdBy?.startsWith('salesforce|')
    const externalAuthorName = isExternalAuthor ? note.createdBy.split('|')[1] : null
    const allowModify = canModify && !note.salesforceTaskId
    const sourceEntityTypeDisplay = sourceEntityType.split('|')[0].replace(/\b\w/g, (l) => l.toUpperCase())

    let sourceEntityUri
    switch (sourceEntityType) {
      case 'tenant':
        sourceEntityUri = `/tenant/tenants/${sourceEntityCuid}`
        break
      case 'program':
        sourceEntityUri = `/program/programs/${sourceEntityCuid}`
        break
      case 'tariff':
        sourceEntityUri = `/admin/tariffs/${sourceEntityCuid}`
        break
      case 'property':
        sourceEntityUri = `/tenant/tenants?propertyId=${sourceEntityCuid}`
        break
      case 'registration':
        sourceEntityUri = `/tenant/onboarding/${sourceEntityCuid}`
        break
    }

    useEffect(() => {
      if (isEditing && editInputRef.current) {
        editInputRef.current.focus()
        editInputRef.current.setSelectionRange(editedBody.length, editedBody.length)
        // editInputRef.current.style.height = 'auto'
        editInputRef.current.style.height = editInputRef.current.scrollHeight + 5 + 'px'
      }
    }, [isEditing])

    const handleUpdate = async () => {
      if (editedBody.trim() !== note.body) {
        await onUpdate(note.cuid, editedBody.trim(), note.tags)
      }
      setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleUpdate()
      } else if (e.key === 'Escape') {
        e.stopPropagation()
        setIsEditing(false)
        setEditedBody(note.body.trim())
      }
    }

    return (
      <div
        className={classnames(
          'mt-4 flex min-w-0 flex-row group p-2 rounded-lg border',
          note.isPinned
            ? 'bg-indigo-100 dark:bg-indigo-500/15 border-indigo-500/20 dark:border-indigo-400/20'
            : 'bg-white dark:bg-slate-100/5 border-slate-200 dark:border-slate-700'
        )}
        role="listitem"
      >
        <div className="flex flex-col items-center gap-1">
          <div className={classnames(
            "mt-1 mr-2 min-h-8 min-w-8 h-8 w-8 flex flex-col items-center justify-center rounded-full border p-1 text-xs",
            isExternalAuthor
              ? "border-blue-300 dark:border-blue-500 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300"
              : "border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300"
          )}>
            {(externalAuthorName
              ? externalAuthorName.split(' ').slice(0, 2).map(n => n.charAt(0)).join('')
              : note.author.firstName && note.author.lastName
              ? note.author.firstName.slice(0, 1) + note.author.lastName.slice(0, 1)
              : note.author.email ?? 'Anonymous'
            )
              .slice(0, 2)
              .toUpperCase()}
          </div>
        </div>
        <div className="flex min-w-0 w-full flex-col">
          {isEditing ? (
            <div>
              <textarea
                ref={editInputRef}
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                onKeyDown={handleKeyDown}
                // onBlur={() => {
                //   setIsEditing(false)
                //   setEditedBody(note.body)
                // }}
                // onBlur={handleUpdate}
                className={classnames(getInputStyles())}
                rows={3}
                style={{ lineHeight: 1.4 }}
              />
              <div className="w-full text-xs font-normal text-slate-400 dark:text-slate-300 text-center">Enter to save, Esc to cancel</div>
            </div>
          ) : (
            <div className="flex min-w-0 items-start justify-between gap-2">
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-slate-700 dark:text-slate-100">
                {note.salesforceTaskId ? (
                  <>
                    <strong>{note.body.split('\n\n')[0]}</strong>
                    {note.body.includes('\n\n') && ' — ' + note.body.split('\n\n').slice(1).join(' | ')}
                  </>
                ) : (
                  <NoteBodyText body={note.body} searchTerm={searchTerm} />
                )}
              </span>
              {allowModify && (
                <div className="relative">
                  <div className="absolute p-1 right-0 bg-slate-200 dark:bg-slate-600 rounded-md w-fit flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-slate-400/20 dark:shadow-slate-900/30">
                    <TagChooser note={note} onUpdate={onUpdate} />
                    <button
                      onClick={() => onTogglePin(note)}
                      className="text-xs p-1 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-500/50 text-indigo-500 hover:text-indigo-400 dark:text-indigo-100 dark:hover:text-indigo-200"
                    >
                      <Tooltip
                        content={note.isPinned ? <div className="text-xs whitespace-nowrap">Unpin</div> : <div className="text-xs whitespace-nowrap">Pin</div>}
                        className="text-xs z-10 pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0 dark:border-0"
                      >
                        {note.isPinned ? <BookmarkSlashIcon className="size-4" /> : <BookmarkIcon className="size-4" />}
                      </Tooltip>
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-xs p-1 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-500/50 text-indigo-500 hover:text-indigo-400 dark:text-indigo-100 dark:hover:text-indigo-200"
                    >
                      <Tooltip
                        content={<div className="text-xs whitespace-nowrap">Edit</div>}
                        className="text-xs z-10 pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0 dark:border-0"
                      >
                        <PencilIcon className="size-4" />
                      </Tooltip>
                    </button>
                    <button
                      onClick={() => onDelete(note)}
                      className="text-xs p-1 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-500/50 text-indigo-500 hover:text-indigo-400 dark:text-indigo-100 dark:hover:text-indigo-200"
                    >
                      <Tooltip
                        content={<div className="text-xs whitespace-nowrap">Delete</div>}
                        className="text-xs z-10 pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0 dark:border-0"
                      >
                        <TrashIcon className="size-4" />
                      </Tooltip>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mt-2 inline text-xs font-normal text-slate-500 dark:text-slate-400">
            <span className="inline whitespace-nowrap">
              <Tooltip
                content={formatDistanceToNow(new Date(note.createdAt), { addSuffix: true }).replace(/^\w/, (c) => c.toUpperCase())}
                placement="bottom"
                className="inline text-xs z-10 pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0 dark:border-0"
              >
                <span className="inline">{format(new Date(note.createdAt), "EEE MMM d, yyyy 'at' h:mm aa")}</span>
              </Tooltip>
            </span>
            <span className="break-words">
              {' by '}
              <span className={isExternalAuthor ? "text-blue-600 dark:text-blue-400" : ""}>
                {externalAuthorName
                  ? `${externalAuthorName} (Salesforce)`
                  : note.author.firstName && note.author.lastName
                  ? `${note.author.firstName} ${note.author.lastName}`
                  : note.author.email != null
                  ? note.author.email
                  : 'Anonymous'}
              </span>
            </span>
            {note.sourceEntityName && (
              <span className="inline">
                {' for '}
                <span className="inline text-xs font-normal text-slate-500 dark:text-slate-400">
                  {sourceEntityTypeDisplay}{' '}
                  <Link to={sourceEntityUri ?? ''} className="inline text-xs font-normal text-indigo-500 dark:text-indigo-300 hover:underline">
                    {note.sourceEntityName}
                  </Link>
                </span>
              </span>
            )}
          </div>
          {note.tags.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-y-1">
              <span className="inline-block text-xs font-normal text-slate-500 dark:text-slate-400">Tags:</span>
              {note.tags.map((tag) => (
                <span
                  key={tag}
                  className="mt-1 ml-1 inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300"
                >
                  {tag}
                  {allowModify && (
                    <button
                      onClick={() => {
                        onUpdate(
                          note.cuid,
                          note.body,
                          note.tags.filter((t) => t !== tag)
                        )
                      }}
                      type="button"
                      className="group relative -mr-1 size-3.5 rounded-sm hover:bg-gray-500/20 dark:hover:bg-gray-400/20"
                    >
                      <span className="sr-only">Remove</span>
                      <svg
                        viewBox="0 0 14 14"
                        className="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75 dark:stroke-gray-400/50 dark:group-hover:stroke-gray-400/75"
                      >
                        <path d="M4 4l6 6m0-6l-6 6" />
                      </svg>
                      <span className="absolute -inset-1" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }
)

export default NoteItem
